import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChecklistIcon from "@mui/icons-material/ChecklistRounded";
import DeleteIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import TaskIcon from "@mui/icons-material/TaskAltRounded";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import type { ObjectChecklist, ObjectClassDefinition, ObjectFieldValues } from "../../../../api/types/objectTypes";
import ObjectFilterDescription from "../../../object-filters/ObjectFilterDescription";
import {
  getConditionFields,
  getConditionFieldSelectOptions,
  getConditionObjectLabel,
} from "../../../object-filters/objectFilterHelpers";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";
import DeleteChecklistDialog from "./DeleteChecklistDialog";

interface Props {
  checklist: ObjectChecklist;
  relatedObjectDefinitions: Record<string, ObjectClassDefinition>;
  taskTemplates: ObjectFieldValues[];
  onEdit: () => void;
}

const ChecklistPreviewCard = ({ checklist, relatedObjectDefinitions, taskTemplates, onEdit }: Props) => {
  const { hasEditPermissions, objectDefinition, onUpdateObjectDefinition } = useObjectDefinitionContext();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [expanded, setExpanded] = useState(false);

  const tasks = useMemo(
    () =>
      checklist.taskTemplateIds
        .map((id) => taskTemplates.find((t) => t.id === id))
        .filter((t): t is ObjectFieldValues => t !== undefined),
    [checklist.taskTemplateIds, taskTemplates]
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Avatar variant="rounded" sx={{ height: 32, width: 32 }}>
              <ChecklistIcon color="action" />
            </Avatar>
            <Typography variant="subtitle1">{checklist.name}</Typography>
          </Stack>

          {hasEditPermissions && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Button variant="outlined" color="secondary" startIcon={<EditIcon />} onClick={onEdit}>
                Edit
              </Button>
              <Tooltip title="Delete" arrow>
                <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1.5 }} />

        <Stack spacing={1}>
          {checklist.conditions.map((c, index) => {
            const conditionFields = getConditionFields(c.objectType, objectDefinition, relatedObjectDefinitions);
            const conditionField = conditionFields.find((f) => f.id === c.field.fieldId);

            return (
              <ObjectFilterDescription
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${c.field.fieldId}_${index}`}
                filter={c.field}
                availableFields={conditionFields}
                selectOptions={getConditionFieldSelectOptions(conditionField)}
                objectTypePrefix={getConditionObjectLabel(c.objectType, objectDefinition, relatedObjectDefinitions)}
                startLabel={index === 0 ? "Where" : "And"}
              />
            );
          })}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ alignItems: "center", mt: 1.5, cursor: "pointer" }}
        >
          <IconButton size="small">
            {expanded ? <ArrowDropDownRoundedIcon fontSize="small" /> : <ArrowRightRoundedIcon fontSize="small" />}
          </IconButton>
          <TaskIcon color="action" />
          <Typography>
            Tasks: <strong>{checklist.taskTemplateIds.length}</strong>
          </Typography>
        </Stack>
        <Collapse in={expanded}>
          <List dense disablePadding>
            {tasks.map((task) => (
              <ListItem key={task.id} sx={{ pl: 4.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckRoundedIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={task.displayValues.primaryValue}
                  secondary={task.displayValues.secondaryValue}
                  sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      </CardContent>
      <DeleteChecklistDialog
        objectType={objectDefinition.objectType}
        checklist={checklist}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={(checklists) => onUpdateObjectDefinition({ ...objectDefinition, checklists })}
      />
    </Card>
  );
};

export default ChecklistPreviewCard;
